import { SCATTER_PLOT_SYSTEM_DEFAULT_STATE } from 'ChartApp/ts/apps/scatter-plot/state/constants';
import { CORE_COLOR_MAP } from 'NewAngular/constants/core.constants';
import { ScatterPlotChartOptionValue, ScatterPlotLookbackType, ScatterPlotPlotType, ScatterPlotPresentationOptionValue, ScatterPlotPresentationValidModes, } from 'ChartApp/ts/enums/scatter-plot-common.enum';
export const ASSESSMENT_SCATTER_PLOT_ZOOM_OPTIONS_TO_LABEL = {
    [ScatterPlotLookbackType.oneYear]: '1Y',
    [ScatterPlotLookbackType.threeYears]: '3Y',
    [ScatterPlotLookbackType.fiveYears]: '5Y',
    [ScatterPlotLookbackType.tenYears]: '10Y',
};
export const ASSESSMENT_SCATTER_PLOT_PARAMS = {
    ...SCATTER_PLOT_SYSTEM_DEFAULT_STATE,
    [ScatterPlotChartOptionValue.displayAllNames]: true,
    [ScatterPlotPresentationOptionValue.legendOrientation]: 'vertical',
    mode: ScatterPlotPresentationValidModes.imageCode,
    plotType: ScatterPlotPlotType.pointInTime,
    lookback: ScatterPlotLookbackType.tenYears,
    xAxisDate: '',
    yAxisDate: '',
    xAxisMetric: '',
    yAxisMetric: '',
    plotOptions: '',
    securities: '',
};
export const BEST_WORST_GENERAL_CHART_PARAMS = {
    annotations: '',
    endDate: '',
    partner: 'best_worst_returns',
    latestDataCutoff: '',
    metricOverrideMapping: '',
    metrics: '',
    pointInTimeDate: '',
    pointInTimeLag: '',
    includeCurrentYearDate: true,
    removeLeadingIncompleteSamples: false,
    resampleFrequency: '',
    resampleMethod: '',
    securities: '',
    startDate: '',
    xAxis: 'category',
    xAxisLabelOverride: '',
    xAxisLabelStyle: 'short',
    yAxisLabelStyle: 'short',
    periodicity: '',
    categorization: '',
    colors: '',
};
export const BEST_WORST_CHART_OPTIONS = {
    chart: {
        height: 215,
    },
    plotOptions: {
        column: {
            groupPadding: 0.4,
        },
    },
    xAxis: {
        visible: false,
        crosshair: false,
    },
    colors: ['#568cd8', '#9abae8', '#ffa870', '#ffc6a1'],
    tooltip: {
        enabled: false,
    },
};
export const BAC_KEYS = [
    'Cash',
    'Stock',
    'Bond',
    'Convertible',
    'Preferred',
    'Other',
];
export const UNKNOWN_ERROR_MESSAGE = 'An unknown error occurred. Please try again later.';
// RTQ's main color is blue
export const RTQ_COLOR_MAP = {
    ...CORE_COLOR_MAP,
    1: {
        code: '#568cd8',
    },
    3: {
        code: '#6833c9',
    },
};
/**
 * Return the risk profiles names as: first suggested risk profile, (if set) compare risk profile then others.
 * This is to match a color scheme of blue (suggested) vs orange (compared) when comparing.
 *
 * @param scoreRanges
 * @param suggestedRiskProfile
 * @param compareRiskProfile
 * @returns
 */
export const sortedScoreRanges = (scoreRanges, suggestedRiskProfile, compareRiskProfile) => {
    return [...scoreRanges].sort((a, b) => {
        if (a.risk_profile.benchmark_symbol === suggestedRiskProfile.benchmark_symbol)
            return -1;
        if (b.risk_profile.benchmark_symbol === suggestedRiskProfile.benchmark_symbol)
            return 1;
        if (compareRiskProfile) {
            if (a.risk_profile.benchmark_symbol === compareRiskProfile.benchmark_symbol)
                return -1;
            if (b.risk_profile.benchmark_symbol === compareRiskProfile.benchmark_symbol)
                return 1;
        }
        return 0;
    });
};
export const buildCompareModalInstructionText = (clientName, suggestedProfileName, compareProfileName) => {
    return `<b>${clientName}'s</b> results suggest a <b>${suggestedProfileName}</b> risk profile. 
        You're overriding the suggested profile with <b>${compareProfileName}</b> risk profile instead. 
        The original results will remain recorded, and it will be noted that the suggested profile was overridden. 
        Please provide a brief note below explaining the reason for this change.`;
};
export const buildBroadAssetClassBreakdown = (riskProfile, bacBreakdown) => {
    const { broad_asset_class_targets } = riskProfile;
    const compareRiskProfileBAC = [...broad_asset_class_targets]
        .map((bacTarget) => {
        return {
            ...bacTarget,
            target: Math.round(bacBreakdown[bacTarget.name] * 100) / 100 || 0,
        };
    })
        .sort((a, b) => BAC_KEYS.indexOf(a.name) - BAC_KEYS.indexOf(b.name));
    const assetAllocationData = BAC_KEYS.map((name, index) => {
        const y = Math.round(bacBreakdown[name] * 100) / 100 || 0;
        const color = CORE_COLOR_MAP[index + 1].code;
        return {
            color,
            name,
            y,
        };
    });
    return [compareRiskProfileBAC, JSON.stringify({ chart_data: assetAllocationData })];
};
export const rtqBulletClassMap = Object.keys(RTQ_COLOR_MAP).reduce((accumulator, key) => {
    let keySwapped = key;
    if (key === '1') {
        keySwapped = '3';
    }
    if (key === '3') {
        keySwapped = '1';
    }
    accumulator[+key] = `bullet-${keySwapped}`;
    return accumulator;
}, {});
export const scoreRangesLabelPositions = (sortedScoredRanges, riskProfile) => {
    const suggestedScoreRange = sortedScoredRanges.find((range) => range.risk_profile.id === riskProfile.id);
    if (suggestedScoreRange === undefined) {
        return null;
    }
    let minScoreRange;
    let maxScoreRange;
    [minScoreRange] = sortedScoredRanges;
    maxScoreRange = minScoreRange;
    for (let i = 0; i < sortedScoredRanges.length; i += 1) {
        const range = sortedScoredRanges[i];
        if (range.min_value < minScoreRange.min_value) {
            minScoreRange = range;
        }
        if (range.max_value > maxScoreRange.max_value) {
            maxScoreRange = range;
        }
    }
    const suggestedMinValueNormalized = suggestedScoreRange.min_value - minScoreRange.min_value;
    const suggestedMaxValueNormalized = suggestedScoreRange.max_value - minScoreRange.min_value;
    const maxScoreRangeMaxValueNormalized = maxScoreRange.max_value - minScoreRange.min_value;
    const suggestedLeftValue = (suggestedMinValueNormalized / maxScoreRangeMaxValueNormalized) * 100;
    const suggestedLeftValueMax = (suggestedMaxValueNormalized / maxScoreRangeMaxValueNormalized) * 100;
    const suggestedRPLabelLeft = suggestedLeftValue + (suggestedLeftValueMax - suggestedLeftValue) / 2;
    const suggestedRightValue = 100 - (suggestedMaxValueNormalized / maxScoreRangeMaxValueNormalized) * 100;
    return {
        minScoreRange,
        maxScoreRange,
        suggestedScoreRange,
        suggestedLeftValue,
        suggestedRPLabelLeft,
        suggestedRightValue,
        maxScoreRangeMaxValueNormalized,
    };
};
/**
 * `client_portfolio` query param constant value used when generating registration risk profile comparison PDF exports
 *      that indicates that the registration's aggregate system portfolio should be used as the comparison security
 *  Mirrors `COMPARISON_CLIENT_PORTFOLIO_ALL` at `apps/risk_tolerance_questionnaire/constants.py`
 */
export const COMPARISON_CLIENT_PORTFOLIO_ALL = 'all';
